import React, { useEffect, useState, useRef } from "react";

import { useNavigate } from "react-router-dom";

import axiosInstance from "../api/axiosInstance";

import {
  getMonthWiseBookings,
  getVehicleGroupWise,
  getPendingSummary,
  getPendingList,
  getDutySummary,
  getDutyTypeWise,
  getVehicleUtilization,
  getMonthlyDutyTrend,
  getDashboardKpis,
} from "../services/dashboardService";

import { useClientInfo, useAppContext } from "../utils/commonImports";

import MonthWiseChart from "../components/dashboard/MonthWiseChart";

import GroupWiseChart from "../components/dashboard/GroupWiseChart";

import DutySummaryChart from "../components/dashboard/DutySummaryChart";

import DutyTypeBarChart from "../components/dashboard/DutyTypeBarChart";

import VehicleUtilizationChart from "../components/dashboard/VehicleUtilizationChart";

import MonthlyDutyTrend from "../components/dashboard/MonthlyDutyTrend";

import PendingListModal from "../components/dashboard/PendingListModal";

const glassCard =
  "min-w-0 rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-shadow duration-200 hover:shadow-md";

const HomePage = () => {
  const { clientId } = useClientInfo();
  const { fyid } = useAppContext();

  // Prevent overlapping dashboard and payment-status requests.
const dashboardRequestRef = useRef({ id: 0 });

const paymentRequestRef = useRef({
  subscription: false,
  driverApp: false,
  mobileApp: false,
});

  const navigate = useNavigate();

  // =====================================

  // DASHBOARD STATES

  // =====================================

  const [monthData, setMonthData] = useState([]);

  const [groupData, setGroupData] = useState([]);

  const [pendingSummary, setPendingSummary] = useState({});

  const [pendingList, setPendingList] = useState([]);

  const [showPendingModal, setShowPendingModal] = useState(false);

  const [loading, setLoading] = useState(true);

  const [dutySummary, setDutySummary] = useState({});

  const [dutyTypeData, setDutyTypeData] = useState([]);

  const [vehicleUtilizationData, setVehicleUtilizationData] = useState([]);

  const [monthlyDutyTrendData, setMonthlyDutyTrendData] = useState([]);

  const [dashboardKpis, setDashboardKpis] = useState({
    today_bookings: 0,

    confirmed_bookings: 0,

    today_tripsheets: 0,

    active_vehicles: 0,
  });

  // =====================================

  // SUBSCRIPTION / ACTIVATION STATES

  // =====================================

  const [subscriptionInvoice, setSubscriptionInvoice] = useState(null);

  const [subscriptionChecking, setSubscriptionChecking] = useState(true);

  // =====================================

  // DRIVER APP PAYMENT STATES

  // =====================================

  const [driverAppInvoice, setDriverAppInvoice] = useState(null);

  const [driverAppChecking, setDriverAppChecking] = useState(true);

  // =====================================

  // =====================================
  // MOBILE APP PAYMENT STATES
  // =====================================

  const [mobileAppInvoice, setMobileAppInvoice] = useState(null);
  const [mobileAppChecking, setMobileAppChecking] = useState(true);

  // INITIAL LOAD + REFRESH

  //

  // NOTE: all three loaders (dashboard, subscription status,

  // driver app payment status) are defined inside this effect

  // and guarded with a `mounted` flag, mirroring the pattern

  // used in Sidebar.jsx. Without this guard, a request that

  // resolves after the component unmounts (e.g. user navigates

  // away mid-request, or mid the 30s poll) would still call

  // setState on an unmounted component.

  // =====================================

useEffect(() => {
  let mounted = true;
 

  // Prevent an indefinite loading screen if clientId or fyid
  // is temporarily unavailable.
  if (!clientId || !fyid) {
    setLoading(false);
    return () => {
      mounted = false;
    };
  }

  // =====================================
  // LOAD DASHBOARD
  // =====================================

  const loadDashboard = async () => {
    // Prevent the 30-second refresh from overlapping an
    // earlier dashboard request.
   if (!mounted) return;

const requestKey = `${clientId}:${fyid}`;

if (dashboardRequestRef.current.active) {
  if (dashboardRequestRef.current.activeKey !== requestKey) {
    // Financial year changed: queue the request for the newly selected year.
    dashboardRequestRef.current.pending = loadDashboard;

    console.log("Dashboard refresh queued for new financial year:", {
      previousKey: dashboardRequestRef.current.activeKey,
      requestedKey: requestKey,
    });
  }

  return;
}

dashboardRequestRef.current.active = true;
dashboardRequestRef.current.activeKey = requestKey;

    try {
      console.log("DASHBOARD REQUEST STARTED", {
        clientId,
        fyid,
        time: new Date().toISOString(),
      });

      const requests = [
        ["Month Wise Bookings", getMonthWiseBookings(clientId, fyid)],
        ["Vehicle Group Wise", getVehicleGroupWise(clientId, fyid)],
        ["Pending Summary", getPendingSummary(clientId, fyid)],
        ["Duty Summary", getDutySummary(clientId, fyid)],
        ["Duty Type Wise", getDutyTypeWise(clientId, fyid)],
        ["Vehicle Utilization", getVehicleUtilization(clientId, fyid)],
        ["Monthly Duty Trend", getMonthlyDutyTrend(clientId, fyid)],
        ["Dashboard KPIs", getDashboardKpis(clientId, fyid)],
      ];

      const results = await Promise.all(
        requests.map(async ([name, request]) => {
          try {
            const response = await request;

            console.log(`DASHBOARD SUCCESS: ${name}`, {
              status: response.status,
            });

            return response;
          } catch (error) {
            console.error(`DASHBOARD FAILED: ${name}`, {
              message: error.message,
              code: error.code,
              status: error.response?.status,
              responseData: error.response?.data,
              url: error.config?.url,
            });

            throw error;
          }
        }),
      );

      if (!mounted) return;

      const [m, g, s, d, dt, vu, md, kpi] = results;

      setMonthData(m?.data || []);
      setGroupData(g?.data || []);
      setPendingSummary(s?.data || {});
      setDutySummary(d?.data || {});
      setDutyTypeData(dt?.data || []);
      setVehicleUtilizationData(vu?.data || []);
      setMonthlyDutyTrendData(md?.data || []);

      setDashboardKpis({
        today_bookings: Number(kpi?.data?.today_bookings || 0),
        confirmed_bookings: Number(kpi?.data?.confirmed_bookings || 0),
        today_tripsheets: Number(kpi?.data?.today_tripsheets || 0),
        active_vehicles: Number(kpi?.data?.active_vehicles || 0),
      });

      console.log("DASHBOARD LOAD COMPLETED", {
        clientId,
        fyid,
        time: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Dashboard Load Error", {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        responseData: error.response?.data,
        url: error.config?.url,
      });
    } finally {
     dashboardRequestRef.current.active = false;
dashboardRequestRef.current.activeKey = null;

if (mounted) {
  setLoading(false);
}

// Run the latest queued request if the financial year changed.
const pendingDashboardRequest = dashboardRequestRef.current.pending;
dashboardRequestRef.current.pending = null;

if (pendingDashboardRequest) {
  pendingDashboardRequest();
}
    }
  };

  // =====================================
  // LOAD SUBSCRIPTION / ACTIVATION STATUS
  // =====================================
// =====================================
// LOAD SUBSCRIPTION PAYMENT STATUS
// =====================================

const loadSubscriptionStatus = async () => {
  if (paymentRequestRef.current.subscription) return;

  paymentRequestRef.current.subscription = true;

  try {
    setSubscriptionChecking(true);

    const response = await axiosInstance.get(
      "/api/clients/subscription-pending",
    );

    if (!mounted) return;

    if (response.data?.success && response.data?.invoice) {
      setSubscriptionInvoice(response.data.invoice);
    } else {
      setSubscriptionInvoice(null);
    }
  } catch (error) {
    console.error("Subscription Status Error:", {
      message: error.message,
      status: error.response?.status,
      responseData: error.response?.data,
      code: error.code,
    });

    if (mounted) {
      setSubscriptionInvoice(null);
    }
  } finally {
    paymentRequestRef.current.subscription = false;

    if (mounted) {
      setSubscriptionChecking(false);
    }
  }
};

// =====================================
// LOAD DRIVER APP PAYMENT STATUS
// =====================================

const loadDriverAppPaymentStatus = async () => {
  if (paymentRequestRef.current.driverApp) return;

  paymentRequestRef.current.driverApp = true;

  try {
    setDriverAppChecking(true);

    const response = await axiosInstance.get(
      "/api/clients/driver-app-payment-pending",
    );

    if (!mounted) return;

    if (response.data?.success && response.data?.invoice) {
      setDriverAppInvoice(response.data.invoice);
    } else {
      setDriverAppInvoice(null);
    }
  } catch (error) {
    console.error("Driver App Payment Status Error:", {
      message: error.message,
      status: error.response?.status,
      responseData: error.response?.data,
      code: error.code,
    });

    if (mounted) {
      setDriverAppInvoice(null);
    }
  } finally {
    paymentRequestRef.current.driverApp = false;

    if (mounted) {
      setDriverAppChecking(false);
    }
  }
};

  // =====================================
  // LOAD MOBILE APP PAYMENT STATUS
  // =====================================

  const loadMobileAppPaymentStatus = async () => {
    try {
      setMobileAppChecking(true);

      const response = await axiosInstance.get(
        "/api/clients/mobile-app-payment-pending",
      );

      if (!mounted) return;

      if (response.data?.success && response.data?.invoice) {
        setMobileAppInvoice(response.data.invoice);
      } else {
        setMobileAppInvoice(null);
      }
    } catch (error) {
      console.error("Mobile App Payment Status Error:", {
        message: error.message,
        status: error.response?.status,
        responseData: error.response?.data,
        code: error.code,
      });

      if (mounted) {
        setMobileAppInvoice(null);
      }
    } finally {
      if (mounted) {
        setMobileAppChecking(false);
      }
    }
  };

  // =====================================
  // INITIAL LOAD
  // =====================================

  loadDashboard();
  loadSubscriptionStatus();
  loadDriverAppPaymentStatus();
  loadMobileAppPaymentStatus();

  // =====================================
  // REFRESH EVERY 30 SECONDS
  // =====================================

  const interval = setInterval(() => {
    if (!mounted) return;

    loadDashboard();
    loadSubscriptionStatus();
    loadDriverAppPaymentStatus();
    loadMobileAppPaymentStatus();
  }, 30000);

  // =====================================
  // CLEANUP
  // =====================================

  return () => {
    mounted = false;
    clearInterval(interval);
  };
}, [clientId, fyid]);


  // =====================================

  // LOAD PENDING BOOKING LIST

  //

  // Accepts an optional filter ("today" | "overdue") so that

  // clicking the "Today" or "Overdue" stat shows a filtered

  // list instead of the same unfiltered list as "Total".

  //

  // ASSUMPTION: getPendingList(clientId, filter) accepts an

  // optional second argument on the backend/service side. If

  // the service does not yet support this, it will simply be

  // ignored server-side and all three stats will keep behaving

  // like "Total" until that support is added.

  // =====================================

const loadPendingList = async (filter = "all") => {
  try {
    const res = await getPendingList(clientId, fyid, filter);
    setPendingList(res?.data || []);
    setShowPendingModal(true);
  } catch (error) {
    console.error("Pending List Error", error);
  }
};

  // =====================================

  // LOADING

  // =====================================

  if (loading) {
    return (
      <div className="flex min-h-56 items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 text-sm font-medium text-slate-500 shadow-sm">
        <span className="mr-3 inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#0f6f8f]" />
        Loading dashboard...
      </div>
    );
  }

  // =====================================

  // PENDING BOOKING SELECT

  // =====================================

  const handlePendingSelect = (bookingId) => {
    setShowPendingModal(false);

    navigate("/bookings/add", {
      state: {
        bookingId,

        mode: "edit",
      },
    });
  };

  // =====================================

  // OPEN PAYMENT

  // =====================================

  const handleSubscriptionPayment = () => {
    if (!subscriptionInvoice) {
      alert("No pending payment found.");

      return;
    }

    navigate("/subscription-payment", {
      state: {
        invoice: subscriptionInvoice,
      },
    });
  };

  // =====================================

  // OPEN DRIVER APP PAYMENT

  // =====================================

  const handleDriverAppPayment = () => {
    if (!driverAppInvoice) {
      alert("No pending Driver App payment found.");

      return;
    }

    navigate("/driver-app-payment", {
      state: {
        invoice: driverAppInvoice,
      },
    });
  };

  // =====================================
  // OPEN MOBILE APP PAYMENT
  // =====================================

  const handleMobileAppPayment = () => {
    if (!mobileAppInvoice) {
      alert("No pending Mobile App payment found.");
      return;
    }

    navigate("/subscription-payment", {
      state: {
        invoice: mobileAppInvoice,
        billingSource: "MOBILE_APP",
      },
    });
  };
  // =====================================

  // INVOICE TYPE

  // =====================================

  const invoiceType = String(
    subscriptionInvoice?.invoice_type || "",
  ).toUpperCase();

  const isActivationInvoice = invoiceType === "ACTIVATION";

  const isSubscriptionInvoice = invoiceType === "SUBSCRIPTION";

  // =====================================

  // PAYMENT UNDER REVIEW

  // =====================================

  const paymentUnderReview = subscriptionInvoice?.payment?.status === "PENDING";

  // =====================================

  // DRIVER APP PAYMENT UNDER REVIEW

  // =====================================

  const driverAppPaymentUnderReview =
    driverAppInvoice?.payment?.status === "PENDING";

  // =====================================

  // AMOUNT

  // =====================================

  const amountDue = Number(
    subscriptionInvoice?.balance_amount ??
      subscriptionInvoice?.total_due ??
      subscriptionInvoice?.balance ??
      0,
  );

  // =====================================

  // DRIVER APP AMOUNT

  // =====================================

  const driverAppAmountDue = Number(
    driverAppInvoice?.balance_amount ??
      driverAppInvoice?.total_due ??
      driverAppInvoice?.balance ??
      0,
  );

  // =====================================

  // RETURN

  // =====================================

  return (
    <div className="min-h-full space-y-2 bg-[#f3f7fc] p-2 sm:p-2.5 lg:p-3">
      {/* =========================================



          ACTIVATION / SUBSCRIPTION PAYMENT ALERT



      ========================================= */}

      {!subscriptionChecking && subscriptionInvoice && (
        <div
          className={`



            rounded-2xl



            border



            shadow-[0_4px_16px_rgba(0,0,0,0.10)]



            p-4



            ${
              isActivationInvoice
                ? "border-red-300 bg-gradient-to-r from-red-50 to-orange-50"
                : "border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50"
            }



          `}
        >
          {/* HEADER */}

          <div
            className="



              flex



              flex-col



              md:flex-row



              md:items-center



              md:justify-between



              gap-3



            "
          >
            <div>
              <p
                className={`



                  text-base



                  font-bold



                  ${isActivationInvoice ? "text-red-700" : "text-orange-800"}



                `}
              >
                {paymentUnderReview
                  ? "Payment Under Review"
                  : isActivationInvoice
                    ? "Software Activation Payment Required"
                    : "Subscription Payment Required"}
              </p>

              <p
                className="



                  text-sm



                  text-gray-600



                  mt-1



                "
              >
                {paymentUnderReview
                  ? "Your payment has been submitted and is waiting for administrator approval."
                  : isActivationInvoice
                    ? "Your software activation invoice is pending payment."
                    : "Your subscription invoice is pending payment."}
              </p>
            </div>

            {/* BUTTON / STATUS */}

            {paymentUnderReview ? (
              <span
                className="



                  px-4



                  py-2



                  rounded-full



                  bg-yellow-100



                  text-yellow-800



                  text-xs



                  font-bold



                  whitespace-nowrap



                "
              >
                PAYMENT UNDER REVIEW
              </span>
            ) : (
              <button
                type="button"
                onClick={handleSubscriptionPayment}
                className={`



                  px-6



                  py-3



                  rounded-xl



                  text-white



                  text-sm



                  font-bold



                  shadow



                  transition



                  whitespace-nowrap



                  ${
                    isActivationInvoice
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-blue-600 hover:bg-blue-700"
                  }



                `}
              >
                {isActivationInvoice ? "ACTIVATE NOW" : "MAKE PAYMENT"}
              </button>
            )}
          </div>

          {/* =====================================



              INVOICE INFORMATION



          ===================================== */}

          <div
            className="



              mt-4



              grid



              grid-cols-2



              md:grid-cols-5



              gap-3



            "
          >
            {/* INVOICE TYPE */}

            <div>
              <p className="text-xs text-gray-500">Invoice Type</p>

              <p
                className="



                  font-semibold



                  text-gray-800



                "
              >
                {isActivationInvoice
                  ? "ACTIVATION"
                  : isSubscriptionInvoice
                    ? "SUBSCRIPTION"
                    : "-"}
              </p>
            </div>

            {/* INVOICE */}

            <div>
              <p className="text-xs text-gray-500">Invoice No</p>

              <p
                className="



                  font-semibold



                  text-gray-800



                "
              >
                {subscriptionInvoice.invoice_no || "-"}
              </p>
            </div>

            {/* AMOUNT */}

            <div>
              <p className="text-xs text-gray-500">Amount Due</p>

              <p
                className={`



                  font-bold



                  text-lg



                  ${isActivationInvoice ? "text-red-600" : "text-blue-600"}



                `}
              >
                ₹
                {amountDue.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </p>
            </div>

            {/* DUE DATE */}

            <div>
              <p className="text-xs text-gray-500">Due Date</p>

              <p
                className="



                  font-semibold



                  text-gray-800



                "
              >
                {subscriptionInvoice.due_date
                  ? new Date(subscriptionInvoice.due_date).toLocaleDateString(
                      "en-GB",
                    )
                  : "-"}
              </p>
            </div>

            {/* BILLING TYPE */}

            <div>
              <p className="text-xs text-gray-500">Billing</p>

              <p
                className="



                  font-semibold



                  text-gray-800



                "
              >
                {subscriptionInvoice.billing_type || "-"}
              </p>
            </div>
          </div>

          {/* =====================================



              ACTIVATION BREAKUP



              NOTE: the ₹15,000 activation fee below is



              hardcoded on the frontend. If this fee ever



              changes server-side, this breakup will silently



              go stale (and "Subscription" is derived as



              amountDue - 15000 rather than a real backend



              value). Ideally the API returns an itemized



              breakup instead.



          ===================================== */}

          {isActivationInvoice && (
            <div
              className="



                mt-4



                rounded-xl



                bg-white



                border



                border-red-100



                p-3



              "
            >
              <p
                className="



                  text-sm



                  font-bold



                  text-gray-800



                  mb-2



                "
              >
                Activation Bill Breakup
              </p>

              <div
                className="



                  flex



                  justify-between



                  text-sm



                  py-1



                "
              >
                <span>Activation Fee</span>

                <span className="font-semibold">₹15,000.00</span>
              </div>

              <div
                className="



                  flex



                  justify-between



                  text-sm



                  py-1



                "
              >
                <span>Subscription</span>

                <span className="font-semibold">
                  ₹
                  {Math.max(amountDue - 15000, 0).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>

              <div
                className="



                  border-t



                  border-gray-200



                  mt-2



                  pt-2



                  flex



                  justify-between



                  font-bold



                "
              >
                <span>Total</span>

                <span className="text-red-600">
                  ₹
                  {amountDue.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          )}

          {/* =====================================



              PAYMENT UNDER REVIEW



          ===================================== */}

          {paymentUnderReview && (
            <div
              className="



                mt-3



                rounded-lg



                bg-yellow-100



                border



                border-yellow-200



                px-3



                py-2



                text-sm



                text-yellow-800



              "
            >
              Payment submitted successfully. Please wait for administrator
              approval.
            </div>
          )}
        </div>
      )}

      {/* =========================================



    DRIVER APP PAYMENT ALERT



================================================================================= */}

      {!driverAppChecking && driverAppInvoice && (
        <div
          className="



        rounded-2xl



        border



        border-blue-300



        bg-gradient-to-r



        from-blue-50



        to-indigo-50



        shadow-[0_4px_16px_rgba(0,0,0,0.10)]



        p-4



      "
        >
          {/* HEADER */}

          <div
            className="



          flex



          flex-col



          md:flex-row



          md:items-center



          md:justify-between



          gap-3



        "
          >
            <div>
              <p
                className="



              text-base



              font-bold



              text-blue-800



            "
              >
                {driverAppPaymentUnderReview
                  ? "Driver App Payment Under Review"
                  : "Driver App Payment Required"}
              </p>

              <p
                className="



              text-sm



              text-gray-600



              mt-1



            "
              >
                {driverAppPaymentUnderReview
                  ? "Your Driver App payment has been submitted and is waiting for administrator approval."
                  : "Your Driver App activation invoice is pending payment."}
              </p>
            </div>

            {/* BUTTON / STATUS */}

            {driverAppPaymentUnderReview ? (
              <span
                className="



              px-4



              py-2



              rounded-full



              bg-yellow-100



              text-yellow-800



              text-xs



              font-bold



              whitespace-nowrap



            "
              >
                PAYMENT UNDER REVIEW
              </span>
            ) : (
              <button
                type="button"
                onClick={handleDriverAppPayment}
                className="



              px-6



              py-3



              rounded-xl



              bg-blue-600



              hover:bg-blue-700



              text-white



              text-sm



              font-bold



              shadow



              transition



              whitespace-nowrap



            "
              >
                MAKE DRIVER APP PAYMENT
              </button>
            )}
          </div>

          {/* =====================================



          DRIVER APP INVOICE INFORMATION



      ===================================== */}

          <div
            className="



          mt-4



          grid



          grid-cols-2



          md:grid-cols-5



          gap-3



        "
          >
            {/* INVOICE TYPE */}

            <div>
              <p
                className="



              text-xs



              text-gray-500



            "
              >
                Invoice Type
              </p>

              <p
                className="



              font-semibold



              text-gray-800



            "
              >
                DRIVER APP
              </p>
            </div>

            {/* INVOICE */}

            <div>
              <p
                className="



              text-xs



              text-gray-500



            "
              >
                Invoice No
              </p>

              <p
                className="



              font-semibold



              text-gray-800



            "
              >
                {driverAppInvoice.invoice_no || "-"}
              </p>
            </div>

            {/* DRIVER COUNT */}

            <div>
              <p
                className="



              text-xs



              text-gray-500



            "
              >
                Drivers
              </p>

              <p
                className="



              font-semibold



              text-gray-800



            "
              >
                {Number(driverAppInvoice.driver_count || 0)}
              </p>
            </div>

            {/* AMOUNT */}

            <div>
              <p
                className="



              text-xs



              text-gray-500



            "
              >
                Amount Due
              </p>

              <p
                className="



              font-bold



              text-lg



              text-blue-600



            "
              >
                ₹
                {driverAppAmountDue.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </p>
            </div>

            {/* DUE DATE */}

            <div>
              <p
                className="



              text-xs



              text-gray-500



            "
              >
                Due Date
              </p>

              <p
                className="



              font-semibold



              text-gray-800



            "
              >
                {driverAppInvoice.due_date
                  ? new Date(driverAppInvoice.due_date).toLocaleDateString(
                      "en-GB",
                    )
                  : "-"}
              </p>
            </div>
          </div>

          {/* =====================================



          DRIVER LIST



      ===================================== */}

          {driverAppInvoice.drivers?.length > 0 && (
            <div
              className="



            mt-4



            rounded-xl



            bg-white



            border



            border-blue-100



            p-3



          "
            >
              <p
                className="



              text-sm



              font-bold



              text-gray-800



              mb-2



            "
              >
                Driver App Activation
              </p>

              <div
                className="



              grid



              grid-cols-1



              md:grid-cols-2



              gap-2



            "
              >
                {driverAppInvoice.drivers.map((driver, index) => (
                  <div
                    key={index}
                    className="



                    flex



                    justify-between



                    items-center



                    bg-gray-50



                    rounded-lg



                    px-3



                    py-2



                  "
                  >
                    <div>
                      <p
                        className="



                        font-semibold



                        text-sm



                      "
                      >
                        {driver.driver_name}
                      </p>

                      <p
                        className="



                        text-xs



                        text-gray-500



                      "
                      >
                        {driver.mobile}
                      </p>
                    </div>

                    <span
                      className="



                      text-xs



                      text-orange-600



                      font-semibold



                    "
                    >
                      PAYMENT PENDING
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* =====================================



          PAYMENT UNDER REVIEW MESSAGE



      ===================================== */}

          {driverAppPaymentUnderReview && (
            <div
              className="
            mt-3
            rounded-lg
            bg-yellow-100
            border
            border-yellow-200
            px-3
            py-2
            text-sm
            text-yellow-800
          "
            >
              Driver App payment submitted successfully. Please wait for
              administrator approval.
            </div>
          )}
        </div>
      )}

      {/* =========================================
    MOBILE APP PAYMENT ALERT
========================================= */}
      {!mobileAppChecking && mobileAppInvoice && (
        <div className="mb-3 rounded-2xl border border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 shadow-[0_4px_16px_rgba(0,0,0,0.10)]">
          {/* HEADER */}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-base font-bold text-blue-800">
                {mobileAppInvoice.payment?.status === "PENDING"
                  ? "Mobile App Payment Under Review"
                  : "Mobile App Payment Required"}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {mobileAppInvoice.payment?.status === "PENDING"
                  ? "Your Mobile App payment has been submitted and is waiting for administrator approval."
                  : "Your Mobile App activation invoice is pending payment."}
              </p>
            </div>

            {mobileAppInvoice.payment?.status === "PENDING" ? (
              <span className="whitespace-nowrap rounded-full bg-yellow-100 px-4 py-2 text-xs font-bold text-yellow-800">
                PAYMENT UNDER REVIEW
              </span>
            ) : (
              <button
                type="button"
                onClick={handleMobileAppPayment}
                className="whitespace-nowrap rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white shadow transition hover:bg-blue-700"
              >
                MAKE MOBILE APP PAYMENT
              </button>
            )}
          </div>

          {/* MOBILE APP INVOICE INFORMATION */}
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            <div>
              <p className="text-xs text-gray-500">Invoice Type</p>
              <p className="font-semibold text-gray-800">MOBILE APP</p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Invoice No</p>
              <p className="break-words font-semibold text-gray-800">
                {mobileAppInvoice.invoice_no || "-"}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Users</p>
              <p className="font-semibold text-gray-800">
                {Number(
                  mobileAppInvoice.user_count ??
                    mobileAppInvoice.users_count ??
                    mobileAppInvoice.registered_users?.length ??
                    mobileAppInvoice.users?.length ??
                    1,
                )}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Amount Due</p>
              <p className="text-lg font-bold text-blue-600">
                ₹
                {Number(
                  mobileAppInvoice.balance_amount ??
                    mobileAppInvoice.total_due ??
                    0,
                ).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Due Date</p>
              <p className="font-semibold text-gray-800">
                {mobileAppInvoice.due_date
                  ? new Date(mobileAppInvoice.due_date).toLocaleDateString(
                      "en-GB",
                      { timeZone: "UTC" },
                    )
                  : "-"}
              </p>
            </div>
          </div>

          {/* REGISTERED MOBILE APP USERS */}
          {(() => {
            const users =
              mobileAppInvoice.registered_users ?? mobileAppInvoice.users ?? [];

            return users.length > 0 ? (
              <div className="mt-4 rounded-xl border border-blue-100 bg-white p-3">
                <p className="mb-2 text-sm font-bold text-gray-800">
                  Mobile App Activation
                </p>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {users.map((user, index) => (
                    <div
                      key={user.mobile_app_user_id ?? user.id ?? index}
                      className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="break-words text-sm font-semibold text-gray-800">
                          {user.name ?? user.user_name ?? "Registered User"}
                        </p>
                        <p className="break-all text-xs text-gray-500">
                          {user.email ?? user.email_id ?? "Email not available"}
                        </p>
                      </div>

                      <span className="shrink-0 text-xs font-semibold text-orange-600">
                        PAYMENT PENDING
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* PAYMENT UNDER REVIEW MESSAGE */}
          {mobileAppInvoice.payment?.status === "PENDING" && (
            <div className="mt-3 rounded-lg border border-yellow-200 bg-yellow-100 px-3 py-2 text-sm text-yellow-800">
              Mobile App payment submitted successfully. Please wait for
              administrator approval.
            </div>
          )}
        </div>
      )}

      {/* ================= KPI CARDS ================= */}

      {/* ================= FIRST ROW: KPI CARDS + BOOKING STATUS ================= */}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-5">
        {/* KPI CARDS */}
        {[
          {
            label: "Today's Bookings",
            value: dashboardKpis.today_bookings,
            note: "Bookings reporting today",
            icon: "▣",
            tone: "from-sky-500 to-cyan-400",
            ink: "text-sky-700",
            surface: "from-sky-50 via-sky-50 to-sky-100/90",
            borderTone: "border-sky-100",
          },
          {
            label: "Confirmed Bookings",
            value: dashboardKpis.confirmed_bookings,
            note: "All confirmed bookings",
            icon: "✓",
            tone: "from-emerald-500 to-green-400",
            ink: "text-emerald-700",
            surface: "from-emerald-50 via-green-50 to-emerald-100/90",
            borderTone: "border-emerald-100",
          },
          {
            label: "Today's Tripsheets",
            value: dashboardKpis.today_tripsheets,
            note: "Duties opened today",
            icon: "▤",
            tone: "from-amber-400 to-orange-400",
            ink: "text-amber-700",
            surface: "from-amber-50 via-orange-50 to-amber-100/90",
            borderTone: "border-amber-100",
          },
          {
            label: "Active Vehicles",
            value: dashboardKpis.active_vehicles,
            note: "Vehicles marked active",
            icon: "▰",
            tone: "from-violet-500 to-purple-400",
            ink: "text-violet-700",
            surface: "from-violet-50 via-purple-50 to-violet-100/90",
            borderTone: "border-violet-100",
          },
        ].map((item) => (
          <div
            key={item.label}
            className={`relative isolate flex min-w-0 items-center gap-1.5 overflow-hidden rounded-2xl border ${item.borderTone} bg-gradient-to-br ${item.surface} px-2 py-1.5 shadow-sm transition-shadow duration-200 hover:shadow-md`}
          >
            {/* Decorative Background */}
            <div
              aria-hidden="true"
              className={`pointer-events-none absolute -bottom-6 -right-3 h-12 w-16 rotate-[-18deg] rounded-[45%] bg-gradient-to-br ${item.tone} opacity-[0.12]`}
            />

            {/* Icon */}
            <div
              className={`relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br ${item.tone} text-base font-bold text-white shadow-sm`}
            >
              {item.icon}
            </div>

            {/* KPI Details */}
            <div className="relative min-w-0 flex-1">
              <p
                className={`truncate text-[11px] font-semibold leading-tight ${item.ink}`}
              >
                {item.label}
              </p>

              <p className="text-lg font-bold leading-tight tracking-tight text-slate-900">
                {item.value}
              </p>

              <p className="text-[9px] leading-tight text-slate-500">
                {item.note}
              </p>
            </div>
          </div>
        ))}

        {/* ================= BOOKING STATUS ================= */}

        <section className="min-w-0 overflow-hidden rounded-lg border border-rose-100 bg-gradient-to-br from-white via-rose-50 to-pink-50 px-2 py-1.5 shadow-sm transition-shadow duration-200 hover:shadow-md">
          {/* Header */}
          <div className="mb-1 flex items-center justify-between gap-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-rose-100 text-sm text-rose-600">
                ▤
              </span>

              <h2 className="truncate text-xs font-bold text-rose-700">
                Booking Status
              </h2>
            </div>

            <button
              type="button"
              onClick={() => navigate("/bookings")}
              title="View all bookings"
              className="shrink-0 rounded-md bg-rose-100 px-1.5 py-1 text-[9px] font-semibold leading-tight text-rose-700 transition hover:bg-rose-200"
            >
              View
            </button>
          </div>

          {/* Pending Booking Counts */}
          <div className="grid grid-cols-3 divide-x divide-rose-200/80">
            {/* Total Pending */}
            <button
              type="button"
              onClick={() => loadPendingList()}
              className="min-w-0 rounded-sm px-0.5 py-0.5 text-center transition hover:bg-rose-100/60"
            >
              <span className="block text-[9px] leading-tight text-slate-600">
                Total Pending
              </span>

              <span className="mt-0.5 block text-base font-bold leading-tight text-rose-700">
                {pendingSummary?.total_pending || 0}
              </span>
            </button>

            {/* Today's Pending */}
            <button
              type="button"
              onClick={() => loadPendingList("today")}
              className="min-w-0 rounded-sm px-0.5 py-0.5 text-center transition hover:bg-rose-100/60"
            >
              <span className="block text-[9px] leading-tight text-slate-600">
                Today's Pending
              </span>

              <span className="mt-0.5 block text-base font-bold leading-tight text-orange-600">
                {pendingSummary?.today_pending || 0}
              </span>
            </button>

            {/* Overdue Pending */}
            <button
              type="button"
              onClick={() => loadPendingList("overdue")}
              className="min-w-0 rounded-sm px-0.5 py-0.5 text-center transition hover:bg-rose-100/60"
            >
              <span className="block text-[9px] leading-tight text-slate-600">
                Overdue Pending
              </span>

              <span className="mt-0.5 block text-base font-bold leading-tight text-red-600">
                {pendingSummary?.overdue_pending || 0}
              </span>
            </button>
          </div>
        </section>
      </div>

      {/* ================= BOOKING ANALYSIS ================= */}
      <div className="grid grid-cols-1 gap-1.5 xl:grid-cols-12">
        {/* Wide monthly booking chart */}
        <section
          className={`${glassCard} flex min-h-[165px] flex-col bg-gradient-to-br from-white via-sky-50/50 to-white p-2 xl:col-span-6`}
        >
          <div className="mb-1 flex items-center justify-between gap-1.5">
            <h2 className="text-xs font-bold text-slate-800">
              Month Wise Bookings
            </h2>
            <span
              className="rounded-md bg-sky-100 p-1 text-sky-700"
              aria-hidden="true"
            >
              ▥
            </span>
          </div>
          <div className="relative min-h-[120px] flex-1">
            <MonthWiseChart key={JSON.stringify(monthData)} data={monthData} />
          </div>
        </section>

        {/* Vehicle group booking distribution */}
        <section
          className={`${glassCard} flex min-h-[165px] flex-col bg-gradient-to-br from-white via-violet-50/40 to-white p-2 xl:col-span-3`}
        >
          <div className="mb-1 flex items-center justify-between gap-1.5">
            <h2 className="text-xs font-bold text-slate-800">
              Vehicle Group Wise Bookings
            </h2>
            <span
              className="rounded-md bg-violet-100 p-1 text-violet-700"
              aria-hidden="true"
            >
              ◉
            </span>
          </div>
          <div className="relative min-h-[120px] flex-1">
            <GroupWiseChart key={JSON.stringify(groupData)} data={groupData} />
          </div>
        </section>

        {/* Duty type comparison */}
        <section
          className={`${glassCard} flex min-h-[165px] flex-col bg-gradient-to-br from-white via-amber-50/40 to-white p-2 xl:col-span-3`}
        >
          <div className="mb-1 flex items-center justify-between gap-1.5">
            <h2 className="text-xs font-bold text-slate-800">Duty Type Wise</h2>
            <span
              className="rounded-md bg-amber-100 p-1 text-amber-700"
              aria-hidden="true"
            >
              ▥
            </span>
          </div>
          <div className="relative min-h-[120px] flex-1">
            <DutyTypeBarChart
              key={JSON.stringify(dutyTypeData)}
              data={dutyTypeData}
            />
          </div>
        </section>
      </div>

      {/* ================= OPERATIONS ANALYSIS ================= */}
      <div className="grid grid-cols-1 gap-1.5 xl:grid-cols-12">
        {/* Duty summary */}
        <section
          className={`${glassCard} flex min-h-[140px] flex-col bg-gradient-to-br from-white via-emerald-50/40 to-white p-2 xl:col-span-3`}
        >
          <div className="mb-1 flex items-center justify-between gap-1.5">
            <h2 className="text-xs font-bold text-slate-800">Duty Summary</h2>
            <button
              type="button"
              onClick={() => navigate("/tripsheets")}
              className="shrink-0 rounded-lg bg-emerald-100 px-1.5 py-1 text-[10px] font-semibold text-emerald-800 transition hover:bg-emerald-200"
            >
              View Tripsheets →
            </button>
          </div>
          <div className="relative min-h-[115px] flex-1">
            <DutySummaryChart
              key={JSON.stringify(dutySummary)}
              summary={dutySummary}
            />
          </div>
        </section>

        {/* Vehicle utilization */}
        <section
          className={`${glassCard} flex min-h-[140px] flex-col bg-gradient-to-br from-white via-blue-50/40 to-white p-2 xl:col-span-3`}
        >
          <div className="mb-1 flex items-center justify-between gap-1.5">
            <h2 className="text-xs font-bold text-slate-800">
              Vehicle Utilization
            </h2>
            <span
              className="rounded-md bg-blue-100 p-1 text-blue-700"
              aria-hidden="true"
            >
              ▰
            </span>
          </div>
          <div className="relative min-h-[115px] flex-1">
            <VehicleUtilizationChart
              key={JSON.stringify(vehicleUtilizationData)}
              data={vehicleUtilizationData}
            />
          </div>
        </section>

        {/* Monthly duty trend */}
        <section
          className={`${glassCard} flex min-h-[140px] flex-col bg-gradient-to-br from-white via-indigo-50/40 to-white p-2 xl:col-span-6`}
        >
          <div className="mb-1 flex items-center justify-between gap-1.5">
            <h2 className="text-xs font-bold text-slate-800">
              Monthly Duty Trend
            </h2>
            <span
              className="rounded-md bg-indigo-100 p-1 text-indigo-700"
              aria-hidden="true"
            >
              ⌁
            </span>
          </div>
          <div className="relative min-h-[115px] flex-1">
            <MonthlyDutyTrend
              key={JSON.stringify(monthlyDutyTrendData)}
              data={monthlyDutyTrendData}
            />
          </div>
        </section>
      </div>

      {/* ================= PENDING LIST ================= */}

      {showPendingModal && (
        <PendingListModal
          data={pendingList}
          onClose={() => setShowPendingModal(false)}
          onSelectBooking={handlePendingSelect}
        />
      )}
    </div>
  );
};

export default HomePage;
